import React, { ReactElement, useState } from "react";

import WarningCircleSVG from "../assets/img/svg/warning-circle.svg";

import wasteCleanersJPEG from "../assets/img/cleaners/waste-cleansing-cleaners.jpg";
import footPathCleanersJPEG from "../assets/img/cleaners/waste-cleansing-footpath.jpg";
import cleaningHoseJPEG from "../assets/img/cleaners/waste-cleansing-hose.jpg";
import garbageTruckJPEG from "../assets/img/cleaners/waste-garbage-trucks.jpg";

import sliderPhotoExampleJPEG from "../assets/img/slider-photo-example.jpg";
import galleryPhotoExampleJPEG from "../assets/img/gallery-photo-example.jpg";

import MoreInfoBlock from "../components/MoreInfoBlock";
import { Link, useHistory } from "react-router-dom";
import HeroImageSlider from "../components/HeroImageSlider";
import CleaningActionImageSlider from "../components/CleaningActionImageSlider";
import { StreetSearchBox, SeoHelmet } from "../components";

import { StreetSweepingSchedule } from "../models";
import { useScrollToTop } from "../hooks";

export default function LandingPage(): ReactElement {
  useScrollToTop();

  const heroImages = [
    sliderPhotoExampleJPEG,
    galleryPhotoExampleJPEG,
    garbageTruckJPEG,
  ];

  const cleaningImages = [
    wasteCleanersJPEG,
    footPathCleanersJPEG,
    cleaningHoseJPEG,
    garbageTruckJPEG,
  ];

  const counterDisplay = (count: number): string => {
    return count < 10 ? `0${count}` : `${count}`;
  };

  const history = useHistory();

  const [currentSlideIndex, setCurrentSlideIndex] = useState(1);
  const [lookupStreet, setLookupStreet] = useState<StreetSweepingSchedule>();
  const [lookupError, setLookupError] = useState<string | null>(null);

  return (
    <main className="main">
      <SeoHelmet
        title="Street Sweeping - City of Canterbury Bankstown"
        description="Find out when your street was last swept, as well as the expected time frame when your street will be swept again."
      />
      {/* Page Hero */}
      <section className="main-hero main-hero_custom-indent">
        <div className="main-hero__substrate">
          <div className="main-hero__container container">
            <div className="main-hero__box">
              <div className="main-hero__descr">
                <h1 className="main-hero__title h1">Street Sweeping Tracker</h1>
                <h2 className="main-hero__subtitle h4">
                  Find out when your street will next be swept
                </h2>
                <form className="main-hero__search search-street">
                  <StreetSearchBox
                    onSelectionChange={setLookupStreet}
                    handleLookupError={setLookupError}
                    inputFieldClassName="search-street__input"
                  />
                  <button
                    className="search-street__submit button button_color_yellow button_shadow_default"
                    onClick={() => {
                      if (lookupStreet) {
                        const { streetName, suburb, postcode } = lookupStreet;
                        history.push({
                          pathname: "/results",
                          search: `streetName=${streetName}&suburb=${suburb}&postcode=${postcode}`,
                          state: {
                            searchStreet: lookupStreet,
                          },
                        });
                      }
                    }}
                  >
                    <span className="button__content">Go</span>
                  </button>
                </form>
                <Link to="/results" hidden />
                {lookupError && (
                  <div style={{ marginTop: "10px" }}>{lookupError}</div>
                )}
              </div>
              <div className="main-hero__slider hero-slider">
                <div className="hero-slider__inner js-hero-slider">
                  <HeroImageSlider images={heroImages} />
                </div>
              </div>
              {/* Logo */}
              {/* <SpriteIcon iconName="logo-path" className="main-hero__logo" /> */}
              {/* /Logo */}
            </div>
          </div>
        </div>
        {/* Report */}
        <div className="main-hero__container container">
          <div className="main-hero__report submit-report">
            <div className="submit-report__icon">
              <img
                src={WarningCircleSVG}
                alt="Warning Icon"
                className="submit-report__icon-img"
              />
            </div>
            <div className="submit-report__descr">
              <h3 className="submit-report__title h4">Submit a report</h3>
              <div className="submit-report__text">
                If you see illegally dumped rubbish or anything else you think
                we have missed, please call us on{" "}
                <a href="tel:0297979000">9707 9000</a> or{" "}
                <a
                  href="https://www.cbcity.nsw.gov.au/onlineservices/customer-service-request"
                  target="_blank"
                  rel="noreferrer"
                >
                  submit an online report.
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* /Report */}
      </section>
      {/* /Page Hero */}
      {/* Sections */}
      <section className="section">
        <div className="sections__container container">
          <div className="plaintext">
            <h3>Street Sweeping</h3>
            <p>
              There are almost 2,400 streets in our city and we aim to sweep
              each street at least once every six weeks. Some streets are swept
              more frequently than others depending on the location and amount
              of rubbish and leaf litter in the street. Streets located within
              our main town centres are swept every night.
            </p>
            <p>
              Our large street sweeper trucks are fitted with GPS data systems
              meaning you can now use the above search tool to see when your
              street was last swept as well as the expected time frame for the
              street to be swept again.
            </p>
          </div>
        </div>
      </section>
      {/* /Sections */}
      {/* Cleaners */}
      <section className="cleaners">
        <div className="cleaners__container container">
          <div className="cleaners__box">
            <div className="cleaners__slider cleaners-slider">
              <div className="cleaners-slider__inner js-cleaners-slider">
                <CleaningActionImageSlider
                  images={cleaningImages}
                  onIndexChanged={(slide: any) => {
                    if (slide && slide.displayIndex) {
                      setCurrentSlideIndex(slide.displayIndex);
                    }
                  }}
                />
              </div>
            </div>
            <div className="cleaners__descr">
              <h2 className="cleaners__title h2">
                Our street sweepers and cleaners in action
              </h2>
              <div className="cleaners__counter h4">
                <span className="js-cleaners-index">
                  {counterDisplay(currentSlideIndex)}
                </span>
                /
                <span className="js-cleaners-slides">
                  {counterDisplay(cleaningImages.length)}
                </span>
              </div>
              {/* Logo */}
              {/* <SpriteIcon iconName="logo-path" className="cleaners__logo" /> */}
              {/* /Logo */}
            </div>
          </div>
        </div>
      </section>
      {/* /Cleaners */}
      <MoreInfoBlock />
    </main>
  );
}
